import ServerErrorHandler from 'app/ErrorHandler';
import request from 'app/api/request';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryKeys as documentQueryKeys } from 'app/api/documents/document-queries';
import { queryKeys as styleQueryKeys } from 'app/api/style-queries';
import { queryKeys as themeQueryKeys } from 'app/api/theme-queries';
import { cancelPendingDesignDraftSaves } from 'app/views/documents/settings/design-configuration/utils/pending-design-saves';

export const queryKeys = {
  draft: 'draft',
};

export const NO_DRAFT = 'NO_DRAFT';

export const draftQueryKey = (documentSuperId) => [queryKeys.draft, String(documentSuperId)];

export function useDraft({ documentSuperId }) {
  return useQuery(
    draftQueryKey(documentSuperId),
    async () => {
      const res = await request.get(`/gaby/design-draft/${documentSuperId}?compiled=true`).catch((err) => {
        if (err.status === 404) {
          return null;
        } else {
          throw err;
        }
      });

      return res?.data || NO_DRAFT;
    },
    {
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}

export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation(
    async (draft) => {
      await request.post(`/gaby/design-draft?compiled=true`, draft);
    },
    {
      onMutate: (draft) => {
        const key = draftQueryKey(draft.documentSuperId);
        const previous = queryClient.getQueryData(key);
        queryClient.setQueryData(key, draft);
        return { previous, key };
      },
      onError: (err, _draft, context) => {
        if (context && context.previous !== undefined) {
          queryClient.setQueryData(context.key, context.previous);
        }
        ServerErrorHandler(err);
      },
      onSuccess: (_, draft) => {
        queryClient.invalidateQueries(queryKeys.draft, draft.documentSuperId);
        queryClient.invalidateQueries(styleQueryKeys.css);
        queryClient.invalidateQueries(themeQueryKeys.appliedStatus);
      },
    }
  );
}

export function useApplyDraft() {
  const queryClient = useQueryClient();
  return useMutation(
    async (pDocument) => {
      await request.post(`/gaby/documents/${pDocument.superId}/actions`, {
        action: 'apply_draft',
        revisionId: pDocument.revisionId,
      });
    },
    {
      onMutate: () => {
        cancelPendingDesignDraftSaves();
      },
      onError: ServerErrorHandler,
      onSuccess: (_, pDocument) => {
        queryClient.invalidateQueries(queryKeys.draft, pDocument.superId);
        queryClient.invalidateQueries(documentQueryKeys.document, pDocument.superId);
        queryClient.invalidateQueries(styleQueryKeys.css);
        queryClient.invalidateQueries(themeQueryKeys.appliedStatus);
      },
    }
  );
}

export function useDiscardDraft() {
  const queryClient = useQueryClient();
  return useMutation(
    async (pDocument) => {
      await request.del(`/gaby/design-draft/${pDocument.superId}`);
    },
    {
      onMutate: () => {
        cancelPendingDesignDraftSaves();
      },
      onError: ServerErrorHandler,
      onSuccess: (_, pDocument) => {
        queryClient.invalidateQueries(queryKeys.draft, pDocument.superId);
        queryClient.invalidateQueries(documentQueryKeys.document, pDocument.superId);
        queryClient.invalidateQueries(styleQueryKeys.css);
        queryClient.invalidateQueries(themeQueryKeys.appliedStatus);
      },
    }
  );
}
